import * as R from 'ramda';
import flatMap from 'lodash/flatMap';
import memoize from 'lodash/memoize';
import { createSelector, defaultMemoize } from 'reselect';

import EntityType from '../../utils/entityType';
import { AppState } from '../reducer';
import { InvoiceGroup, OrderSorting } from '../../components/Orders/utils';
import {
  getInvoiceGroups,
  getInvoices,
  getOrderTotalDue,
  isPaid,
  orderCost,
  orderInvoices,
  sortOrdersByDeliverDate,
  sortOrdersByInvoiceDueDate,
} from '../../utils/invoice';
import { order$, orderCollection$ } from '../orders/selectors';

export const entitiesState = (state: AppState) => state.entities;

// eslint-disable-next-line @typescript-eslint/default-param-last
export function entityCollectionByIds<T>(ids: Array<number | string> = [], entities: Record<string, T>): T[] {
  return R.values(R.pick(ids, entities));
}

export const invoiceEntities$ = createSelector([entitiesState], (state) => state[EntityType.INVOICES]);

export const invoiceCollection$ = createSelector([invoiceEntities$], (invoicesEntities) =>
  Object.values(invoicesEntities)
);

/**
 * Filters invoices on the basis of when the invoiceSummary has PAID status, the amountPaid equals the total regardless
 * of if the user has marked the invoice as paid.
 */
export const allUnpaidInvoices$ = createSelector([invoiceCollection$], (invoices) =>
  invoices.filter((invoice) => !isPaid(invoice))
);

export const allUnpaidOrders$ = createSelector([invoiceEntities$, orderCollection$], (invoiceEntities, orders) =>
  orders.filter((order) => !orderInvoices(order, invoiceEntities).filter((invoice) => isPaid(invoice)).length)
);

export const orderInvoices$ = createSelector(order$, invoiceEntities$, (order, invoiceEntities) =>
  memoize((orderId?: string) => (order(orderId) ? orderInvoices(order(orderId)!, invoiceEntities) : undefined))
);

export const ordersByInvoiceGroup$ = defaultMemoize((group: InvoiceGroup) =>
  createSelector([orderCollection$, invoiceEntities$], (orders, invoiceEntities) =>
    orders.filter((order) => getInvoiceGroups(order, invoiceEntities).includes(group))
  )
);

export const filterOrdersByInvoiceGroup$ = defaultMemoize((group: InvoiceGroup) =>
  createSelector([orderCollection$, invoiceEntities$], (orders, invoiceEntities) =>
    orders.filter((order) => !getInvoiceGroups(order, invoiceEntities).includes(group))
  )
);

export const invoicesByGroup$ = defaultMemoize((group: InvoiceGroup) =>
  createSelector([ordersByInvoiceGroup$(group), invoiceEntities$], (orders, invoicesEntities) =>
    getInvoices(orders, invoicesEntities)
  )
);

export const ordersByInvoiceGroupAndSort$ = defaultMemoize((group: InvoiceGroup, sort: OrderSorting) =>
  createSelector([ordersByInvoiceGroup$(group), invoiceEntities$], (orders, invoices) =>
    sort === OrderSorting.INVOICE_DUE && !R.isEmpty(invoices)
      ? sortOrdersByInvoiceDueDate(orders, invoices)
      : sortOrdersByDeliverDate(orders)
  )
);

export const totalCostOfAllUnpaidOrders$ = createSelector(
  [filterOrdersByInvoiceGroup$(InvoiceGroup.PAID), invoiceEntities$],
  (paidOrders, invoices) => getOrderTotalDue(paidOrders, invoices)
);

export const totalCostOfInvoicesByGroup$ = defaultMemoize((group: InvoiceGroup) =>
  createSelector([invoicesByGroup$(group)], (invoices) =>
    invoices.map(({ total }) => total).reduce((totalA, totalB) => totalA + totalB, 0)
  )
);

/**
 * The total cost of orders filtered by invoice group. Will use the an invoice due total BEFORE an order cost.
 */
export const totalCostOfOrdersByGroup$ = defaultMemoize((group: InvoiceGroup) =>
  createSelector([ordersByInvoiceGroup$(group), invoiceEntities$], (orders, invoicesEntities) =>
    orders.map((order) => orderCost(order, invoicesEntities)).reduce((totalA, totalB) => totalA + totalB, 0)
  )
);

export const idsOfInvoicesByGroup$ = defaultMemoize((group: InvoiceGroup) =>
  createSelector([invoicesByGroup$(group)], (invoices) => flatMap(invoices, ({ id }) => id))
);

export const supplierEntities$ = (state: AppState) => state.entities.suppliers;

export const suppliers$ = createSelector([supplierEntities$], (entities) => Object.values(entities));

export const supplierById$ = defaultMemoize((id?: number | null) =>
  createSelector([supplierEntities$], (entities) => {
    const emptySupplier = {
      id: 0,
      slug: '',
      name: 'No Preference',
      active: false,
      logo: '',
    };

    if (!id) {
      return emptySupplier;
    }

    return entities[id] || emptySupplier;
  })
);

export const supplierBySlug$ = createSelector(suppliers$, (suppliers) =>
  memoize((slug?: string | null) => suppliers.find((supplier) => supplier.slug === slug))
);
